import { useCallback, useEffect, useState } from "react";

export type AsyncState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "error"; message: string };

/**
 * Tiny fetch hook — calls `fetcher` on mount and whenever any item
 * in `deps` changes. Returns the discriminated state union plus a
 * `reload()` to force-refetch (used by the "Retry" button).
 *
 * Deliberately not React Query: this app has ~6 fetches total, and
 * a 30 KB dependency to memoize state we already have feels silly.
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ kind: "loading" });
  const [nonce, setNonce] = useState(0);

  // Stable callback to expose to "Retry" buttons. Bumping `nonce`
  // triggers the effect below, which re-invokes the fetcher.
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetcher()
      .then((data) => {
        if (!cancelled) setState({ kind: "ready", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload };
}
